/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import 'number-flow';
import 'number-flow/group';

import { countdownOptions } from '../options/countdown.options';

/* eslint-disable object-property-newline */

/**
 * @typedef {'upcoming' | 'active' | 'ended'} CountdownStatus
 * @typedef {'days' | 'hours' | 'minutes' | 'seconds'} UnitName
 *
 * @typedef {object} ClockUnitEls
 * @property {HTMLElement}        block      Wrapper holding the number + suffix.
 * @property {HTMLElement | null} separator  Span rendered before this unit when it isn't the first visible unit; null for the first unit in the pool.
 * @property {HTMLElement}        numberFlow `<number-flow>` instance for this unit.
 * @property {HTMLElement}        suffix     Span rendering the unit's suffix (singular/plural-aware).
 *
 * @typedef {object} Clock
 * @property {HTMLElement}                       wrapper  The `<number-flow-group>` root for this clock instance.
 * @property {UnitName[]}                        pool     The active unit pool for this clock (subset of `visibleUnits` ∩ token args).
 * @property {Partial<Record<UnitName, ClockUnitEls>>} unitEls Per-unit DOM references.
 * @property {boolean}                           noSpill  When true, the clock uses canonical decomposition (no mass spillover from higher units outside the pool). Set by the `{clock!:units}` token flag.
 *
 * @typedef {object} CountdownInstance
 * @property {HTMLElement}             element        Wrapper element decorated with `amb-countdown`.
 * @property {object}                  options        Fully-merged options from the compiler. Dates are normalized to epoch ms (or null) on init.
 * @property {CountdownStatus | null}  currentStatus  Last resolved lifecycle status; null on first tick.
 * @property {Clock[]}                 clocks         All `<number-flow-group>` instances rendered inside the wrapper.
 * @property {boolean}                 visible        Maintained by the shared IntersectionObserver. The shared tick interval skips invisible instances; the observer triggers an immediate tick on the false→true transition.
 * @property {number}                  intervalMs     The tick cadence (ms) this instance is registered to. Currently always `TICK_INTERVAL`; reserved for future per-instance cadence.
 *
 * @typedef {object} TickEntry
 * @property {number | null}  handle        `setInterval` handle for this cadence; null when paused (document hidden).
 * @property {Set<string>}    instanceIds   Element `ambid`s ticking at this cadence.
 */

/**
 * Update tick cadence in milliseconds. Sub-second updates aren't useful — none
 * of our supported units expose a tenths digit — so a 1000ms interval is the
 * sweet spot between accuracy and CPU/battery usage.
 *
 * @readonly
 */
const TICK_INTERVAL = 1000;

/**
 * Milliseconds per unit. Used for the time-distribution / overflow logic.
 *
 * @readonly
 */
const MS_PER_UNIT = {
  days: 86_400_000,
  hours: 3_600_000,
  minutes: 60_000,
  seconds: 1_000
};

/**
 * Canonical ordering for the unit pool — largest to smallest. Sliding-window
 * logic and token-arg restoration both rely on this fixed order.
 *
 * @readonly
 * @type {UnitName[]}
 */
const UNIT_ORDER = ['days', 'hours', 'minutes', 'seconds'];

/**
 * Aliases accepted in `{clock:units}` token arguments. The merchant can write
 * any of these and the directive resolves to the canonical unit name.
 *
 * @readonly
 */
const UNIT_ALIASES = {
  d: 'days', day: 'days', days: 'days',
  h: 'hours', hr: 'hours', hrs: 'hours', hour: 'hours', hours: 'hours',
  m: 'minutes', min: 'minutes', mins: 'minutes', minute: 'minutes', minutes: 'minutes',
  s: 'seconds', sec: 'seconds', secs: 'seconds', second: 'seconds', seconds: 'seconds'
};

/**
 * Per-unit digit caps for NumberFlow's smooth roll-over animation. Position 1
 * is the tens digit; position 0 (ones) is unconstrained. The cap is only
 * applied to non-topmost units — the topmost unit can grow arbitrarily large
 * (e.g., 1000+ days, or 200+ hours when `days` is hidden).
 *
 * @readonly
 */
const UNIT_DIGIT_CAPS = {
  hours: { 1: { max: 2 } },
  minutes: { 1: { max: 5 } },
  seconds: { 1: { max: 5 } }
};

/**
 * `{clock}` / `{clock:units}` / `{clock!:units}` token pattern.
 *
 *  - Group 1: optional `!` flag → "no-spill" / canonical-decomposition mode.
 *    Useful for multi-clock labels where mutually-exclusive token pools
 *    must NOT double-count time.
 *      `Started {clock:days}, and {clock!:hours,minutes,seconds} more`
 *      with remaining = 1d 2h 3m 4s renders as
 *      "Started 1d, and 2h 3m 4s more" (instead of the buggy
 *      "Started 1d, and 26h 3m 4s more" the un-flagged form would emit).
 *  - Group 2: optional comma-separated unit-alias list after the colon.
 *
 * @readonly
 */
const CLOCK_TOKEN_REGEX = /\{clock(!)?(?::([^}]+))?\}/g;

/**
 * Per-channel formatter that maps a directive option value to its
 * `countdown-{value}` CSS class. Returns `null` when the option value
 * doesn't warrant a modifier class (e.g., the default `Single Block`
 * layout always emits a class so it is included).
 *
 * The Page Designer server already adds these classes during SSR (no-FOUC)
 * but the directive re-applies them on init for direct ISML / content-asset
 * usage, where no server-side decoration ran. `classList.add` is idempotent
 * so the dual application is cheap and safe.
 *
 * Typography classes (`.h1`–`.h6`, `.sub-title-{size}`, `.text-{size}`)
 * are NOT applied here — they come from the site styleguide and are owned
 * by the Page Designer component (`renderLabelAs` + `fontSize` settings).
 * Direct directive usage in ISML / content assets can add any styleguide
 * typography class on the host element manually; the countdown digits and
 * chrome scale from it via `em` units.
 *
 * @readonly
 * @type {Record<string, (value: string | undefined) => string | null>}
 */
const CLASS_CHANNELS = {
  clockColor: (value) => value ? `countdown-${value}` : null,
  clockStyle: (value) => value ? `countdown-${value}` : null,
  clockLayout: (value) => {
    if (value === 'single_block') return 'countdown-single';
    if (value === 'split_blocks') return 'countdown-split';
    return null;
  },
  pattern: (value) => value ? `countdown-${String(value).replace(/_/g, '-')}` : null
};

/**
 * Countdown directive
 *
 * Drives the entire client-side lifecycle of the Page Designer countdown
 * component AND any standalone `<div amb-countdown>` usage in ISML / content
 * assets:
 *
 *  1. On `init`:
 *     - Parse `startDate` / `endDate` (accepts epoch ms, ISO 8601, `Date`,
 *       numeric strings — see {@link parseDate}).
 *     - Validate the date range (`start < end` when both are set); a bad
 *       range hides the wrapper and skips registration.
 *     - Apply appearance classes (`countdown-{theme}`, `countdown-{style}`,
 *       …). Idempotent — the PD server already added these in the SSR path.
 *     - Register with the shared {@link observer} (visibility tracking) and
 *       a shared per-cadence tick (see {@link ticks}).
 *
 *  2. Each shared tick:
 *     - Resolves the current lifecycle status (UPCOMING / ACTIVE / ENDED)
 *       from `Date.now()` and the parsed dates.
 *     - On status transitions, rebuilds the wrapper's inner HTML from the
 *       appropriate label template — replacing every `{clock}` /
 *       `{clock:units}` / `{clock!:units}` token with a `<number-flow-group>`
 *       clock.
 *     - Distributes the remaining time across each clock's unit pool and
 *       updates each `<number-flow>`. NumberFlow handles the digit animation.
 *     - Applies the sliding-window cap (`maxVisibleUnits`) or leading-zero
 *       trim (`hideZeroUnits`; Page Designer: unchecked `showZeroUnits`) by
 *       toggling per-unit visibility.
 *
 *  3. The shared tick:
 *     - One `setInterval` per unique cadence, regardless of how many
 *       instances are active. All clocks at the same cadence tick at the
 *       SAME `Date.now()` snapshot (perfect synchronization).
 *     - Pauses when the document tab is hidden; resumes (with an immediate
 *       refresh) when it becomes visible again.
 *
 *  4. The shared {@link observer}:
 *     - Tracks viewport visibility for every instance. Off-screen instances
 *     - are skipped during tick to save CPU / battery.
 *     - On invisible → visible transition, the affected instance is ticked
 *       immediately so the user sees fresh values without waiting for the
 *       next interval cycle.
 */
export const countdownDirective = {
  /**
   * @protected
   */
  options: countdownOptions,

  /**
   * Per-instance state, keyed by `element.ambid`.
   *
   * @type {Map<string, CountdownInstance>}
   */
  instances: new Map(),

  /**
   * Per-cadence tick registration. The Map structure makes future per-instance
   * cadence trivial — today every instance ticks at {@link TICK_INTERVAL} so
   * there's only one entry, but the data shape is ready for `1000`, `500`,
   * `250`, … cadences if a future use-case calls for it.
   *
   * @type {Map<number, TickEntry>}
   */
  ticks: new Map(),

  /**
   * Shared IntersectionObserver tracking viewport visibility for ALL instances.
   * Created lazily on the first {@link init} and torn down when the last
   * instance is destroyed.
   *
   * @type {IntersectionObserver | null}
   */
  observer: null,

  /**
   * Shared `visibilitychange` listener (page-tab visibility). Parallel to
   * {@link observer} — pauses every cadence when the document is hidden and
   * resumes them with an immediate tick when visible again.
   *
   * @type {(() => void) | null}
   */
  visibilityListener: null,

  /**
   * Initialize a countdown for the given wrapper element.
   *
   * @param {HTMLElement} element HTML DOM element (carries `amb-countdown`).
   * @param {object}      options Fully-merged options from the compiler.
   */
  init(element, options = {}) {
    // ARIA: the countdown is a "timer" landmark per WAI-ARIA. NumberFlow
    // handles its own internal a11y for the ticking digits, so we don't add
    // `aria-live` on the wrapper to avoid double-announcement on every tick.
    if (!element.hasAttribute('role')) {
      element.setAttribute('role', 'timer');
    }

    // Parse + normalize dates BEFORE validation so string / Date / epoch ms
    // inputs all converge on the same representation.
    const startDate = this.parseDate(options.startDate);
    const endDate = this.parseDate(options.endDate);

    if (startDate != null && endDate != null && startDate >= endDate) {
      console.warn(
        '[amb-countdown] startDate must be before endDate — hiding component.',
        { element, startDate, endDate }
      );
      element.setAttribute('hidden', '');
      return;
    }

    // Persist the parsed values back so every downstream method (computeStatus,
    // getTarget, …) deals with epoch ms.
    const parsedOptions = Object.assign({}, options, { startDate, endDate });

    /** @type {CountdownInstance} */
    const instance = {
      element,
      options: parsedOptions,
      currentStatus: null,
      clocks: [],
      visible: false,
      intervalMs: TICK_INTERVAL
    };

    this.instances.set(element.ambid, instance);

    // Apply appearance classes (idempotent — server already added them in PD).
    this.applyAppearanceClasses(element, parsedOptions);

    // Register with shared module-level systems.
    this.registerVisibilityListener();
    this.registerObserver();
    this.observer.observe(element);

    this.registerTick(instance.intervalMs, element.ambid);

    // Initial tick BEFORE the observer has a chance to fire its first
    // callback. This guarantees the wrapper has correct content on first paint
    // (label + clocks) regardless of viewport position. Subsequent ticks are
    // gated by visibility.
    this.tick(instance, Date.now());
  },

  /**
   * Tear down the countdown for the given element.
   *
   * @param {HTMLElement} element
   */
  destroy(element) {
    const instance = this.instances.get(element.ambid);

    if (!instance) {
      return;
    }

    if (this.observer) {
      this.observer.unobserve(element);
    }
    this.unregisterTick(instance.intervalMs, element.ambid);

    element.innerHTML = '';
    this.instances.delete(element.ambid);

    // Last instance gone → tear down shared module-level resources so the
    // directive leaves no listeners / observers behind.
    if (this.instances.size === 0) {
      this.teardownObserver();
      this.teardownVisibilityListener();
    }
  },

  // ---------- input parsing ----------

  /**
   * Coerce a heterogeneous date input into an epoch-ms number (or null).
   * Accepted forms:
   *
   *  - `null` / `undefined` / empty string → `null` (unconfigured).
   *  - `number` → returned as-is when finite.
   *  - `Date` instance → unwrapped via `.getTime()`.
   *  - numeric string → parsed as epoch ms (`"1735689600000"`); enables
   *    `data-start-date="1735689600000"` style HTML attribute usage where
   *    the value comes through as a string.
   *  - any other string → fed to `new Date(str)` and unwrapped. Browsers
   *    accept ISO 8601 with or without the `Z` suffix, the `YYYY-MM-DD`
   *    short form (treated as UTC midnight), and a few looser variants
   *    (`"2024-07-23 23:07:30"` is browser-dependent — prefer ISO).
   *
   * Returns `null` when the input cannot be parsed instead of throwing —
   * lets the caller decide between "not configured" and "invalid".
   *
   * @protected
   *
   * @param {number | string | Date | null | undefined} value
   *
   * @returns {number | null}
   */
  parseDate(value) {
    if (value == null || value === '') return null;

    if (typeof value === 'number') {
      return isFinite(value) ? value : null;
    }

    if (value instanceof Date) {
      const t = value.getTime();
      return isNaN(t) ? null : t;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') return null;

      // Numeric string → epoch ms. Cover negatives so dates before 1970 work.
      if (/^-?\d+$/.test(trimmed)) {
        const n = Number(trimmed);
        return isFinite(n) ? n : null;
      }

      const t = new Date(trimmed).getTime();
      return isNaN(t) ? null : t;
    }

    return null;
  },

  // ---------- appearance ----------

  /**
   * Apply `countdown` + per-channel modifier classes derived from options.
   * Idempotent — calling `classList.add` on an already-present class is a
   * no-op, so we don't fight the server's SSR class application.
   *
   * @protected
   *
   * @param {HTMLElement} element
   * @param {object}      options
   */
  applyAppearanceClasses(element, options) {
    element.classList.add('countdown');

    for (const channel in CLASS_CHANNELS) {
      const className = CLASS_CHANNELS[channel](options[channel]);

      if (className) {
        element.classList.add(className);
      }
    }
  },

  // ---------- module-level: ticks ----------

  /**
   * @protected
   *
   * @param {number} intervalMs
   * @param {string} ambid
   */
  registerTick(intervalMs, ambid) {
    let entry = this.ticks.get(intervalMs);

    if (!entry) {
      entry = { handle: null, instanceIds: new Set() };
      this.ticks.set(intervalMs, entry);
    }

    entry.instanceIds.add(ambid);
    this.startTickInterval(intervalMs);
  },

  /**
   * @protected
   *
   * @param {number} intervalMs
   * @param {string} ambid
   */
  unregisterTick(intervalMs, ambid) {
    const entry = this.ticks.get(intervalMs);
    if (!entry) return;

    entry.instanceIds.delete(ambid);

    if (entry.instanceIds.size === 0) {
      this.stopTickInterval(intervalMs);
      this.ticks.delete(intervalMs);
    }
  },

  /**
   * @protected
   *
   * @param {number} intervalMs
   */
  startTickInterval(intervalMs) {
    const entry = this.ticks.get(intervalMs);

    if (!entry || entry.handle != null || entry.instanceIds.size === 0 || document.hidden) {
      return;
    }

    entry.handle = setInterval(() => this.runTick(intervalMs), intervalMs);
  },

  /**
   * @protected
   *
   * @param {number} intervalMs
   */
  stopTickInterval(intervalMs) {
    const entry = this.ticks.get(intervalMs);
    if (!entry || entry.handle == null) return;

    clearInterval(entry.handle);
    entry.handle = null;
  },

  /**
   * Run the tick callback for every instance registered at this cadence.
   * Pulls a single `Date.now()` snapshot so all clocks at this cadence see
   * the SAME current time (perfect synchronization).
   *
   * Off-screen instances (per the shared {@link observer}) are skipped to
   * save CPU.
   *
   * @protected
   *
   * @param {number} intervalMs
   */
  runTick(intervalMs) {
    const entry = this.ticks.get(intervalMs);
    if (!entry) return;

    const now = Date.now();
    const skippedInstances = [];
    const processedInstances = [];

    for (const ambid of entry.instanceIds) {
      const instance = this.instances.get(ambid);

      if (!instance || !instance.visible) {
        skippedInstances.push(ambid);
        continue;
      }

      this.tick(instance, now);
      processedInstances.push(ambid);
    }
  },

  // ---------- module-level: tab visibility ----------

  /**
   * @protected
   */
  registerVisibilityListener() {
    if (!this.visibilityListener) {
      this.visibilityListener = () => this.handleVisibilityChange();

      document.addEventListener('visibilitychange', this.visibilityListener);
    }
  },

  /**
   * @protected
   */
  teardownVisibilityListener() {
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);

      this.visibilityListener = null;
    }
  },

  /**
   * @protected
   */
  handleVisibilityChange() {
    if (document.hidden) {
      // Pause every cadence — no need to keep ticking when the user can't
      // see the page.
      for (const intervalMs of this.ticks.keys()) {
        this.stopTickInterval(intervalMs);
      }
    } else {
      // Visible again. Run every cadence ONCE immediately so on-screen
      // clocks update without waiting for the next interval boundary, then
      // resume the interval.
      for (const intervalMs of this.ticks.keys()) {
        this.runTick(intervalMs);
        this.startTickInterval(intervalMs);
      }
    }
  },

  // ---------- module-level: viewport visibility ----------

  /**
   * @protected
   */
  registerObserver() {
    if (this.observer) return;

    this.observer = new IntersectionObserver((entries) => {
      const now = Date.now();

      for (const entry of entries) {
        const instance = this.instances.get(entry.target.ambid);
        if (!instance) continue;

        const wasVisible = instance.visible;
        instance.visible = entry.isIntersecting;

        // Invisible → visible transition: tick immediately so the user
        // sees fresh values without waiting for the next interval cycle.
        if (!wasVisible && instance.visible) {
          this.tick(instance, now);
        }
      }
    }, {
      // A small margin so clocks just below the fold stay accurate while
      // the user is scrolling toward them. Keeps perceived freshness high
      // without keeping deeply off-screen clocks ticking.
      rootMargin: '50px',
      threshold: 0
    });
  },

  /**
   * @protected
   */
  teardownObserver() {
    if (!this.observer) return;

    this.observer.disconnect();
    this.observer = null;
  },

  /**
   * Single tick: resolves the current status, rebuilds the label on state
   * transitions, and pushes the new values into NumberFlow.
   *
   * Accepts an explicit `now` so the shared interval can pass a single
   * `Date.now()` snapshot to all instances at the same cadence (perfect
   * synchronization). Called directly during init / on visibility-change
   * with a freshly-sampled `now`.
   *
   * @protected
   *
   * @param {CountdownInstance} instance
   * @param {number}            [now] Defaults to `Date.now()` when omitted.
   */
  tick(instance, now) {
    if (now == null) {
      now = Date.now();
    }

    const { element, options } = instance;
    const status = this.computeStatus(now, options);

    // Hide whole component for statuses not in showComponentOnStatus.
    const showableStatuses = options.showComponentOnStatus || [];
    if (showableStatuses.indexOf(status) === -1) {
      element.setAttribute('hidden', '');
      return;
    }
    element.removeAttribute('hidden');

    // Rebuild label only on a real status transition.
    if (instance.currentStatus !== status) {
      instance.currentStatus = status;
      this.rebuildLabel(instance, status);
    }

    for (const clock of instance.clocks) {
      this.updateClock(clock, status, now, options);
    }
  },

  // ---------- state ----------

  /**
   * Resolve the current lifecycle status. The `previewState` override only
   * applies in Page Designer Edit/Preview mode.
   *
   * @protected
   *
   * @param {number} now
   * @param {object} options
   *
   * @returns {CountdownStatus}
   */
  computeStatus(now, options) {
    if (options.isPreview && options.previewState && options.previewState !== 'auto') {
      return options.previewState;
    }

    const startDate = options.startDate;
    const endDate = options.endDate;

    if (startDate !== null && now < startDate) {
      return 'upcoming';
    }

    if (endDate !== null && now >= endDate) {
      return 'ended';
    }

    return 'active';
  },

  /**
   * Resolve the clock's target moment for the current status:
   *
   *  - `upcoming`:  the `startDate` (future)  → counts DOWN to start.
   *  - `active` :   the `endDate` if set (future) → counts DOWN to end.
   *                 Otherwise falls back to `startDate` (past) → counts UP
   *                 since the event started. Use-case: open-ended sales /
   *                 "Promotion has been live for X" badges.
   *  - `ended`  :   the `endDate` (past) → counts UP since the event ended.
   *                 Use-case: "Ended Xh Ym ago" recap labels.
   *
   * Returns `null` only when no usable timestamp exists. The directive
   * skips the clock entirely in that case (label text still renders).
   *
   * @protected
   *
   * @param {CountdownStatus} status
   * @param {object}          options
   *
   * @returns {number | null}
   */
  getTarget(status, options) {
    switch (status) {
      case 'upcoming': return options.startDate;
      case 'active': return options.endDate !== null ? options.endDate : options.startDate;
      case 'ended': return options.endDate;
      default: return null;
    }
  },

  /**
   * Resolve the animation direction for the clock based on the resolved
   * status. Mirrors the natural reading of {@link getTarget}: a target in
   * the future counts down (`-1`), a target in the past counts up (`+1`).
   *
   * NumberFlow uses this to pick the correct digit-roll animation when the
   * value changes between ticks.
   *
   * @protected
   *
   * @param {CountdownStatus} status
   * @param {object}          options
   *
   * @returns {-1 | 1}
   */
  getDirection(status, options) {
    switch (status) {
      case 'upcoming': return -1;
      case 'active': return options.endDate !== null ? -1 : 1;
      case 'ended': return 1;
      default: return -1;
    }
  },

  // ---------- label rebuild ----------

  /**
   * Replace the wrapper's inner HTML with the label corresponding to the new
   * status. Every `{clock}` / `{clock:units}` token expands into its own
   * `<number-flow-group>` clock; surrounding text wraps in `<span class="label">`.
   *
   * @protected
   *
   * @param {CountdownInstance} instance
   * @param {CountdownStatus}   status
   */
  rebuildLabel(instance, status) {
    const { element, options } = instance;

    const label = this.getLabel(options, status);
    const target = this.getTarget(status, options);
    const direction = this.getDirection(status, options);
    const clockAllowedForStatus = (options.showClockOnStatus || []).indexOf(status) !== -1;

    // A clock can render only if the merchant allowed it AND we actually have
    // a target to count to. Otherwise the `{clock}` tokens are stripped.
    const renderClock = clockAllowedForStatus && target != null;
    const parts = this.parseLabel(label);
    const hasClockToken = parts.some((part) => part.type === 'clock');

    Array.from(element.childNodes).forEach(node => {
      const isStyleNode = node instanceof HTMLStyleElement;

      if (!isStyleNode) {
        node.remove();
      }
    });

    instance.clocks = [];

    for (const part of parts) {
      if (part.type === 'text') {
        if (part.value !== '') {
          element.appendChild(this.buildLabelText(part.value));
        }
      } else if (part.type === 'clock' && renderClock) {
        const clock = this.buildClock(part.unitsArg, options, direction, part.noSpill);
        element.appendChild(clock.wrapper);
        instance.clocks.push(clock);
      }
    }

    // Fallback: when the label has no `{clock}` token at all but the clock
    // should render for this status, append the clock after the label.
    if (!hasClockToken && renderClock) {
      const clock = this.buildClock(null, options, direction, false);
      element.appendChild(clock.wrapper);
      instance.clocks.push(clock);
    }
  },

  /**
   * @protected
   *
   * @param {object} options
   * @param {CountdownStatus} status
   *
   * @returns {string}
   */
  getLabel(options, status) {
    switch (status) {
      case 'upcoming': return options.labelUpcoming || '';
      case 'active': return options.labelActive || '';
      case 'ended': return options.labelEnded || '';
      default: return '';
    }
  },

  /**
   * Split a label into a flat list of text and clock segments.
   *
   * Each clock segment carries:
   *  - `unitsArg` — the comma-separated alias list after `:`, or `null` when
   *    the token has no argument (`{clock}`).
   *  - `noSpill`  — `true` when the merchant used the `{clock!:units}` form,
   *    asking for canonical (non-spilling) decomposition. Defaults to false
   *    so plain `{clock:units}` retains the established spill behavior
   *    (backward compatible).
   *
   * @protected
   *
   * @param {string} label
   *
   * @returns {Array<{ type: 'text', value: string } | { type: 'clock', unitsArg: string | null, noSpill: boolean }>}
   */
  parseLabel(label) {
    const parts = [];
    let lastIndex = 0;
    let match;

    // The regex has the `g` flag — reset state in case it was used elsewhere.
    CLOCK_TOKEN_REGEX.lastIndex = 0;

    while ((match = CLOCK_TOKEN_REGEX.exec(label)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', value: label.slice(lastIndex, match.index) });
      }
      parts.push({
        type: 'clock',
        // Group 1 captures the optional `!` flag; group 2 captures the
        // optional unit-alias list.
        noSpill: !!match[1],
        unitsArg: match[2] || null
      });
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < label.length) {
      parts.push({ type: 'text', value: label.slice(lastIndex) });
    }

    return parts;
  },

  /**
   * @protected
   *
   * @param {string} text
   *
   * @returns {HTMLElement}
   */
  buildLabelText(text) {
    const span = document.createElement('span');
    span.className = 'label';
    span.textContent = text;

    // Preserve `\n` line breaks when the merchant uses multi-line label copy.
    if (text.indexOf('\n') !== -1) {
      span.style.whiteSpace = 'pre-line';
    }

    return span;
  },

  // ---------- clock build ----------

  /**
   * Build one `<number-flow-group>` clock instance and its per-unit DOM.
   * Token arguments restrict the active pool for THIS clock only.
   *
   * @protected
   *
   * @param {string | null} unitsArg  The raw argument from `{clock:units}` / `{clock!:units}`, or null when the token had no argument.
   * @param {object}        options
   * @param {-1 | 1}        direction Animation trend (-1 = counting down, +1 = counting up). See {@link getDirection}.
   * @param {boolean}       [noSpill] When true the clock will use canonical decomposition (no mass spillover from higher units outside the pool).
   *
   * @returns {Clock}
   */
  buildClock(unitsArg, options, direction = -1, noSpill = false) {
    const pool = this.resolvePool(unitsArg, options.visibleUnits);
    const separator = options.unitSeparator == null ? '' : String(options.unitSeparator);

    const group = document.createElement('number-flow-group');
    group.className = 'clock';

    /** @type {Partial<Record<UnitName, ClockUnitEls>>} */
    const unitEls = {};

    pool.forEach((unit, index) => {
      // Insert a separator span before every unit except the first in the
      // pool. The separator can be hidden imperatively when the unit (or all
      // preceding units) become hidden.
      let separatorEl = null;

      if (index > 0 && separator !== '') {
        separatorEl = document.createElement('span');
        separatorEl.className = 'clock__separator';
        separatorEl.setAttribute('aria-hidden', 'true');
        separatorEl.textContent = separator;
        group.appendChild(separatorEl);
      }

      const block = document.createElement('span');
      block.className = `clock__unit clock__unit--${unit}`;

      const nf = document.createElement('number-flow');
      nf.className = 'clock__value';
      // Counting-down clocks roll digits downward (-1); counting-up clocks
      // roll upward (+1). NumberFlow uses this hint for direction-of-travel
      // animation when the value changes between ticks.
      nf.trend = direction;
      nf.format = { minimumIntegerDigits: options.padDigits ? 2 : 1 };

      const suffixEl = document.createElement('span');
      suffixEl.className = 'clock__suffix';

      block.appendChild(nf);
      block.appendChild(suffixEl);

      group.appendChild(block);

      unitEls[unit] = {
        block,
        separator: separatorEl,
        numberFlow: nf,
        suffix: suffixEl
      };
    });

    return { wrapper: group, pool, unitEls, noSpill };
  },

  /**
   * Compute the active unit pool for a clock instance. The pool is the
   * intersection of the global `visibleUnits` and the (optional) token
   * argument, preserved in canonical order (days → seconds).
   *
   * Unknown aliases are silently dropped from the requested set but a single
   * `console.warn` is emitted so the developer/merchant can catch typos like
   * `{clock:hourz}`.
   *
   * @protected
   *
   * @param {string | null} unitsArg
   * @param {UnitName[] | undefined} globalVisibleUnits
   *
   * @returns {UnitName[]}
   */
  resolvePool(unitsArg, globalVisibleUnits) {
    const globalPool = UNIT_ORDER.filter((unit) =>
      (globalVisibleUnits || UNIT_ORDER).indexOf(unit) !== -1
    );

    if (!unitsArg) {
      return globalPool;
    }

    const requested = new Set();
    const invalid = [];

    for (const rawToken of String(unitsArg).split(',')) {
      const token = rawToken.trim().toLowerCase();

      if (token === '') {
        continue;
      }

      if (UNIT_ALIASES[token]) {
        requested.add(UNIT_ALIASES[token]);
      } else {
        invalid.push(rawToken);
      }
    }

    if (invalid.length > 0) {
      const validAliases = Object.keys(UNIT_ALIASES).join(', ');
      console.warn(
        `[amb-countdown] Ignoring unknown unit alias(es) in {clock:${unitsArg}}: ` +
        `[${invalid.join(', ')}]. Accepted aliases: ${validAliases}.`
      );
    }

    const restricted = globalPool.filter((unit) => requested.has(unit));

    // If the token only listed unknown / globally-disabled units, fall back
    // to the global pool rather than rendering an empty clock.
    return restricted.length > 0 ? restricted : globalPool;
  },

  // ---------- clock update ----------

  /**
   * Push fresh values into every `<number-flow>` of a single clock instance.
   * Also handles per-unit visibility (sliding window / leading-zero trim) and
   * per-value suffix updates (singular/plural for `full_suffix`).
   *
   * @protected
   *
   * @param {Clock}           clock
   * @param {CountdownStatus} status
   * @param {number}          now
   * @param {object}          options
   */
  updateClock(clock, status, now, options) {
    const { pool, unitEls, noSpill } = clock;
    const target = this.getTarget(status, options);

    // `Math.abs` lets the same loop drive both directions: future target
    // (countdown) and past target (count-up). Direction is communicated to
    // NumberFlow via `trend` at build time — see {@link getDirection}.
    const remaining = target != null ? Math.abs(target - now) : 0;

    const { values, effectivePool } = noSpill ?
      this.distributeCanonical(pool, remaining, options) :
      this.distributeRemaining(pool, remaining, options)
    ;
    const displayedSet = new Set(this.computeDisplayedUnits(effectivePool, values, options));

    // First pass: figure out which visible unit is FIRST so we can suppress
    // its separator. Track in a single linear pass.
    let visibleCount = 0;

    for (const unit of pool) {
      const els = unitEls[unit];
      const isVisible = displayedSet.has(unit);

      els.block.toggleAttribute('hidden', !isVisible);

      if (els.separator) {
        // Hide the separator when:
        //  - this unit itself is hidden, OR
        //  - no visible unit has been encountered yet (this is the first visible unit in this tick).
        els.separator.toggleAttribute('hidden', !isVisible || visibleCount === 0);
      }

      if (isVisible) {
        const suffix = this.formatSuffix(unit, values[unit], options);

        visibleCount += 1;

        // The EFFECTIVE topmost unit (first visible in canonical order)
        // must be unbounded — it can grow past its natural max once higher
        // units are collapsed by `unitThresholds` (vd: hours becomes 47+
        // when days is dropped). Lower units keep their natural digit
        // caps (hours 0-23, minutes 0-59, seconds 0-59) so NumberFlow's
        // roll-over animation stays correct.
        els.numberFlow.digits = visibleCount === 1 ? undefined : (UNIT_DIGIT_CAPS[unit] || undefined);
        els.numberFlow.update(values[unit]);

        if (els.suffix.textContent !== suffix) {
          els.suffix.textContent = suffix;
        }
      }
    }
  },

  /**
   * Distribute the `remaining` millisecond mass across the unit pool from
   * largest to smallest. Honors `options.unitThresholds`: a unit whose
   * tentative value falls below its threshold is REMOVED from the
   * effective pool, and its mass overflows into the next smaller unit.
   *
   * Example (`unitThresholds.days = 2`, pool = `[days, hours, minutes,
   * seconds]`, remaining ≈ 36 h):
   *   days  tentative = 1 → 1 < 2 → drop days, mass stays
   *   hours tentative = 36 → keep, remaining drops to leftover
   *   minutes / seconds → distribute the leftover normally
   *
   * Always returns at least one entry in `effectivePool` (the smallest
   * unit of the original pool) so the clock never renders empty when the
   * source pool is non-empty.
   *
   * @protected
   *
   * @param {UnitName[]} pool
   * @param {number}     remaining  Always non-negative (caller normalizes).
   * @param {object}     options
   *
   * @returns {{ values: Record<UnitName, number>, effectivePool: UnitName[] }}
   */
  distributeRemaining(pool, remaining, options) {
    const thresholds = options.unitThresholds || {};
    const values = {};
    const effectivePool = [];

    for (const unit of pool) {
      const ms = MS_PER_UNIT[unit];
      const threshold = Math.max(0, thresholds[unit] | 0);
      const tentative = Math.floor(remaining / ms);

      if (tentative >= threshold) {
        effectivePool.push(unit);
        values[unit] = tentative;
        remaining -= tentative * ms;
      } else {
        // Below threshold: drop the unit and let its mass cascade into the
        // next-smaller unit on the following iteration.
        values[unit] = 0;
      }
    }

    // Edge case: every unit in the pool was below threshold. Restore the
    // smallest unit so the clock isn't rendered empty (the merchant always
    // wants SOMETHING ticking on screen).
    if (effectivePool.length === 0 && pool.length > 0) {
      const fallback = pool[pool.length - 1];
      const ms = MS_PER_UNIT[fallback];
      effectivePool.push(fallback);
      values[fallback] = Math.floor(remaining / ms);
    }

    return { values, effectivePool };
  },

  /**
   * Canonical (no-spill) decomposition. Computes each unit's value from the
   * FULL `UNIT_ORDER` independently of the pool, then exposes only the units
   * the pool requested. Mass that "belongs" to a unit OUTSIDE the pool is
   * NOT cascaded into the pool — it's simply not rendered.
   *
   * Used for the `{clock!:units}` token form so multi-clock labels can carve
   * a single timestamp into mutually-exclusive token pools without double-
   * counting:
   *
   *   "Started {clock:days}, and {clock!:hours,minutes,seconds} more"
   *   remaining = 1d 2h 3m 4s
   *   →  "Started 1d, and 2h 3m 4s more"   ✓ (canonical decomposition)
   *
   * Compare to the spill form:
   *
   *   "Started {clock:days}, and {clock:hours,minutes,seconds} more"
   *   →  "Started 1d, and 26h 3m 4s more"  ← double-counts the day's hours
   *
   * `unitThresholds` still apply: a sub-threshold canonical value drops the
   * unit from the effective pool but its mass disappears (no neighbor
   * inherits it — that's what canonical means). `unitThresholds` thus
   * functions as a pure visibility filter in this mode.
   *
   * @protected
   *
   * @param {UnitName[]} pool
   * @param {number}     remaining  Always non-negative (caller normalizes).
   * @param {object}     options
   *
   * @returns {{ values: Record<UnitName, number>, effectivePool: UnitName[] }}
   */
  distributeCanonical(pool, remaining, options) {
    const thresholds = options.unitThresholds || {};

    // Step 1: canonical decomposition over the FULL unit order. This is the
    // "ground truth" — `r mod day_ms div hour_ms = canonical hours`.
    const allValues = {};
    let r = remaining;

    for (const unit of UNIT_ORDER) {
      const ms = MS_PER_UNIT[unit];
      allValues[unit] = Math.floor(r / ms);
      r -= allValues[unit] * ms;
    }

    // Step 2: filter by pool + thresholds. Sub-threshold units are dropped
    // (they're hidden, NOT spilled — that's the whole point of this mode).
    const values = {};
    const effectivePool = [];

    for (const unit of pool) {
      const value = allValues[unit];
      const threshold = Math.max(0, thresholds[unit] | 0);

      if (value >= threshold) {
        effectivePool.push(unit);
        values[unit] = value;
      } else {
        values[unit] = 0;
      }
    }

    // Edge case: every unit was below threshold. Keep the smallest pool
    // unit visible so the clock isn't blank — same rationale as the spill
    // distribution above.
    if (effectivePool.length === 0 && pool.length > 0) {
      const fallback = pool[pool.length - 1];
      effectivePool.push(fallback);
      values[fallback] = allValues[fallback];
    }

    return { values, effectivePool };
  },

  /**
   * Apply the sliding-window cap (`maxVisibleUnits`) or the leading-zero
   * trim (`hideZeroUnits`) and return the units that should be visible this
   * tick.
   *
   * The `pool` argument here is the EFFECTIVE pool returned by either
   * {@link distributeRemaining} or {@link distributeCanonical} (i.e.,
   * already filtered by `unitThresholds`), so this stage layers visual
   * policies on top of the already-collapsed pool.
   *
   * @protected
   *
   * @param {UnitName[]}                 pool
   * @param {Record<UnitName, number>}   values
   * @param {object}                     options
   *
   * @returns {UnitName[]}
   */
  computeDisplayedUnits(pool, values, options) {
    if (pool.length === 0) {
      return [];
    }

    const max = options.maxVisibleUnits | 0;

    if (max > 0) {
      // Sliding window: anchor at the topmost unit with a non-zero value.
      let topIdx = pool.findIndex((unit) => values[unit] > 0);

      // All zero → anchor at the smallest unit so we still show something
      // (e.g., `00s`).
      if (topIdx === -1) {
        topIdx = pool.length - 1;
      }

      return pool.slice(topIdx, topIdx + max);
    }

    if (options.hideZeroUnits) {
      // Trim leading zero units but always keep at least one (the lowest unit
      // is shown even when zero so the clock doesn't disappear at `00:00:00`).
      const trimmed = pool.slice();
      while (trimmed.length > 1 && values[trimmed[0]] === 0) {
        trimmed.shift();
      }
      return trimmed;
    }

    return pool.slice();
  },

  /**
   * Build the suffix string for a single unit based on the active pattern.
   *
   *  - `digital`      → empty
   *  - `short_suffix` → single-letter (`d` / `h` / `m` / `s`)
   *  - `full_suffix`  → ` ` + singular/plural localized word
   *
   * @protected
   *
   * @param {UnitName} unit
   * @param {number}   value
   * @param {object}   options
   *
   * @returns {string}
   */
  formatSuffix(unit, value, options) {
    const pattern = options.pattern || 'short_suffix';
    const suffixes = options.suffixes || {};

    if (pattern === 'digital') {
      return '';
    }

    if (pattern === 'short_suffix') {
      return (suffixes.short && suffixes.short[unit]) || '';
    }

    if (pattern === 'full_suffix') {
      const full = suffixes.full && suffixes.full[unit];
      if (!full) return '';
      return ' ' + (value === 1 ? full.singular : full.plural);
    }

    return '';
  }
};
